import * as React from 'react';
import { ITenantConfig } from 'data/tenant-config';
import Dialog from './Dialog';
import { brandNames } from 'data/schema';
import { marketFlags, marketNames } from './marketFlags';
import { useNavigate } from 'react-router';

// Brand logos
import AudiLogo from '../../assets/icons/audi/logo.svg';
import VWLogo from '../../assets/icons/vw/logo.svg';
import CupraLogo from '../../assets/icons/cupra/logo.svg';
import BentleyLogo from '../../assets/icons/bentley/logo.svg';
import LamborghiniLogo from '../../assets/icons/lamborghini/logo.svg';

const Carlines = React.lazy(() => import('./Carlines'));
const CarConfigurator = React.lazy(() => import('./CarConfigurator'));
const General = React.lazy(() => import('./General'));
const DGACameraFlight = React.lazy(() => import('./DGACameraFlight'));

export interface ITenantDetailsAdmin {
  tenant: ITenantConfig;
  onDone: () => void;
  tenantName: string;
  onTenantUpdate?: (tenant: ITenantConfig) => void;
  onConfiguratorValidation?: (
    configuratorType: 'DCC' | 'SCC',
    hasLinks: boolean,
    updatedConfig?: ITenantConfig
  ) => void;
}

export type IMarketDashboardTab = 'general' | 'carlines' | 'car-configurator' | 'dga-camera-flight';

export default function MarketDashboard({
  tenant,
  tenantName,
  onTenantUpdate,
  onConfiguratorValidation,
}: ITenantDetailsAdmin) {
  const [tab, setTab] = React.useState<IMarketDashboardTab>('general');
  const [currentTenant, setCurrentTenant] = React.useState<ITenantConfig>(tenant);
  const navigate = useNavigate();

  // Update currentTenant when tenant prop changes (e.g., after external updates)
  React.useEffect(() => {
    setCurrentTenant(tenant);
  }, [tenant]);

  const handleTabChange = (newTab: IMarketDashboardTab) => {
    setTab(newTab);
  };

  const handleTenantUpdate = (updates: Partial<ITenantConfig>) => {
    const updatedTenant = {
      ...currentTenant,
      ...updates,
      flags: updates.flags ? { ...currentTenant.flags, ...updates.flags } : currentTenant.flags,
      sessionTimeout: updates.sessionTimeout
        ? { ...currentTenant.sessionTimeout, ...updates.sessionTimeout }
        : currentTenant.sessionTimeout,
      scheduler: updates.scheduler ? { ...currentTenant.scheduler, ...updates.scheduler } : currentTenant.scheduler,
    };

    setCurrentTenant(updatedTenant);

    // Notify parent component of the update
    if (onTenantUpdate) {
      onTenantUpdate(updatedTenant);
    }
  };

  const brandLogo = brandLogos[currentTenant.brand] || brandLogos['*'];
  const brandLabel = currentTenant.brand === '*' ? 'All Brands' : brandNames[currentTenant.brand];
  const marketLabel =
    currentTenant.market === '*' ? 'All Markets' : `${marketNames[currentTenant.market]} (${currentTenant.market})`;
  const marketFlag = marketFlags[currentTenant.market] || marketFlags['*'];
  const tenantLabel =
    !currentTenant.tenant || currentTenant.tenant === '*' ? 'All Unconfigured Tenants (*)' : tenantName || 'Tenant';

  const handleRun3DE = () => {
    navigate(`/${currentTenant.brand}/${currentTenant.market}/`);
  };

  return (
    <Dialog>
      {/* <!-- Off-canvas menu for mobile, show/hide based on off-canvas menu state. --> */}
      <section className="flex h-full flex-col bg-teal-dark">
        <header className="py-10">
          <div className="max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-x-8">
                <div className="shrink-0">
                  <img src={brandLogo} alt={brandLabel} className="size-20 rounded-full bg-white p-3" />
                </div>
                <div>
                  <h1 className="flex items-center gap-x-3 text-3xl font-bold tracking-tight text-white">
                    {brandLabel}
                    <span className="text-4xl">{marketFlag}</span>
                  </h1>
                  <p className="mt-1 text-sm text-gray-400">
                    {marketLabel} • {tenantLabel}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-x-3">
                <button
                  onClick={handleRun3DE}
                  className="rounded-md bg-white-a10 px-4 py-2 text-sm font-semibold text-white hover:bg-white-a20"
                >
                  Run 3DE
                </button>
              </div>
            </div>
          </div>
        </header>
        <div className="flex flex-1 justify-stretch gap-x-2 overflow-hidden bg-white px-4 py-1 sm:px-6">
          <Sidebar {...{ handleTabChange, tab }} />
          <div className="mt-4 flex-1 overflow-y-auto bg-white">
            {/* Show active tab. It will use React suspense to load the Carlines component when carlines is selected */}
            <React.Suspense fallback={<div>Loading...</div>}>
              {tab === 'carlines' && <Carlines brand={currentTenant.brand} market={currentTenant.market} />}
            </React.Suspense>
            <React.Suspense fallback={<div>Loading...</div>}>
              {tab === 'car-configurator' && (
                <CarConfigurator
                  brand={currentTenant.brand}
                  market={currentTenant.market}
                  tenantId={currentTenant.tenant ?? '*'}
                  onConfiguratorValidation={onConfiguratorValidation}
                />
              )}
            </React.Suspense>
            <React.Suspense fallback={<div>Loading...</div>}>
              {tab === 'general' && <General tenant={currentTenant} onUpdate={handleTenantUpdate} />}
            </React.Suspense>
            <React.Suspense fallback={<div>Loading...</div>}>
              {tab === 'dga-camera-flight' && <DGACameraFlight tenant={currentTenant} onUpdate={handleTenantUpdate} />}
            </React.Suspense>
          </div>
        </div>
      </section>
    </Dialog>
  );
}

const Tab = ({
  name,
  id,
  current,
  onClick,
}: {
  name: string;
  id: string;
  current: boolean;
  onClick: (id: string) => void;
}) => {
  return (
    <li>
      <a
        href="#"
        className={`group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold ${current ? 'bg-gray-100 text-teal' : 'text-gray-700 hover:bg-gray-100 hover:text-teal'}`}
        onClick={() => onClick(id)}
      >
        {name}
      </a>
    </li>
  );
};

const Sidebar = ({
  handleTabChange,
  tab,
}: {
  handleTabChange: (newTab: IMarketDashboardTab) => void;
  tab: IMarketDashboardTab;
}) => {
  return (
    <>
      {/* <!-- Static sidebar for desktop --> */}
      <div className="inset-y-0 z-50 flex w-72 flex-col">
        {/* <!-- Sidebar component, swap this element with another sidebar if you like --> */}
        <div className="flex grow flex-col gap-y-5 overflow-y-auto border-r border-teal-dark bg-white px-2">
          <nav className="mt-8 flex flex-1 flex-col">
            <ul role="list" className="flex flex-1 flex-col gap-y-7">
              <li>
                <ul role="list" className=" space-y-1">
                  <Tab
                    name="General"
                    id="general"
                    current={tab === 'general'}
                    onClick={() => handleTabChange('general')}
                  />
                  <Tab
                    name="Carlines"
                    id="carlines"
                    current={tab === 'carlines'}
                    onClick={() => handleTabChange('carlines')}
                  />
                  <Tab
                    name="Car Configurator"
                    id="car-configurator"
                    current={tab === 'car-configurator'}
                    onClick={() => handleTabChange('car-configurator')}
                  />
                  <Tab
                    name="Digital Giveaway (DGA)"
                    id="dga-camera-flight"
                    current={tab === 'dga-camera-flight'}
                    onClick={() => handleTabChange('dga-camera-flight')}
                  />
                </ul>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </>
  );
};

const brandLogos: Record<string, string> = {
  A: AudiLogo,
  V: VWLogo,
  S: CupraLogo,
  E: BentleyLogo,
  L: LamborghiniLogo,
  N: VWLogo, // VW Nutzfahrzeuge uses VW logo
  C: VWLogo, // Skoda - using VW as fallback (add specific logo if available)
  P: VWLogo, // Porsche - using VW as fallback (add specific logo if available)
  M: VWLogo, // MAN - using VW as fallback (add specific logo if available)
  '*': AudiLogo, // Default fallback
};
